import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cookieBlocked, setCookieBlocked] = useState(false);

  // If the 401 interceptor fires while we're already on /login, the user's
  // browser is almost certainly blocking our auth cookie (Safari ITP,
  // Brave Shields, 3rd-party cookies disabled). Surface a clear explanation
  // instead of letting them refill the form in a silent loop.
  useEffect(() => {
    const handler = () => setCookieBlocked(true);
    window.addEventListener("pbcoach:session-blocked", handler);
    return () => window.removeEventListener("pbcoach:session-blocked", handler);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      const role = res.user?.role;
      const defaultDest = role === "coach" ? "/coach" : role === "admin" ? "/admin" : "/student";
      // Priority: explicit ?next= (from a 401 bounce) > react-router state > role default
      const params = new URLSearchParams(loc.search);
      const nextParam = params.get("next");
      const dest = nextParam || loc.state?.from?.pathname || defaultDest;
      nav(dest, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A] text-white">
      <div className="hidden md:block relative overflow-hidden border-r border-white/10">
        <img
          src="https://images.pexels.com/photos/35688546/pexels-photo-35688546.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/70 to-black" />
        <div className="relative p-12 flex flex-col h-full">
          <Link to="/" className="text-xs uppercase tracking-[0.3em] text-[#38BDF8] font-bold flex items-center gap-2" data-testid="back-home-link">
            <ArrowLeft size={14} /> Back home
          </Link>
          <div className="mt-auto">
            <h2 className="font-display text-5xl font-black uppercase tracking-tighter leading-[0.9]">
              Log in &<br />
              run your<br />
              <span className="text-[#38BDF8]">practice.</span>
            </h2>
            <p className="text-[#A3A3A3] mt-6 max-w-sm">
              Your roster, calendar, AI lesson plans and analytics are waiting.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <Logo className="h-32 w-auto mb-6" testId="login-logo" />
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Step 01 · Identify</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-3">Welcome back</h1>
          <p className="text-[#A3A3A3] mt-3 text-sm">Coaches and students sign in here.</p>

          {cookieBlocked && (
            <div
              className="mt-6 border border-amber-400/40 bg-amber-400/5 p-4 text-sm space-y-2"
              data-testid="cookie-blocked-banner"
              role="alert"
            >
              <div className="text-amber-300 font-bold uppercase tracking-widest text-[10px]">
                Browser is blocking your session cookie
              </div>
              <p className="text-white/90">
                You signed in, but your browser didn't keep the session cookie — so the next
                page immediately logged you out. This usually means strict privacy settings:
              </p>
              <ul className="list-disc pl-5 text-white/80 text-[13px] space-y-1">
                <li><strong>Safari</strong>: Settings → Safari → <em>Prevent Cross-Site Tracking</em> → turn <strong>off</strong> for this site (or toggle globally off, then back on after testing).</li>
                <li><strong>Brave</strong>: tap the lion icon in the address bar → <em>Shields</em> → turn <strong>off</strong> for pbcoach.app (or set Trackers &amp; ads blocking to <em>Standard</em>).</li>
                <li>Make sure <strong>third-party cookies</strong> are allowed for pbcoach.app.</li>
              </ul>
              <p className="text-[#A3A3A3] text-xs">
                Reload the page after adjusting the setting, then sign in again.
              </p>
            </div>
          )}

          <div className="mt-8 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-3 text-lg"
                placeholder="you@coach.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-3 text-lg"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-[#A3A3A3] hover:text-[#38BDF8] uppercase tracking-widest font-bold" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-10 btn-primary w-full py-4 text-sm disabled:opacity-60"
            data-testid="login-submit"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-6 text-sm text-[#A3A3A3]">
            New here? <Link to="/register" className="text-[#38BDF8] font-bold hover:underline" data-testid="register-link">Create account</Link>
          </div>

          <div className="mt-8 p-4 border border-white/10 bg-white/[0.02] text-xs text-[#A3A3A3]" data-testid="demo-accounts">
            <div className="uppercase tracking-widest font-bold text-white/80 mb-2">Demo accounts</div>
            <div>Coach: <span className="text-white">coach@pickleballpro.app</span> / coach123</div>
            <div>Player: <span className="text-white">student@pickleballpro.app</span> / student123</div>
            <div className="mt-2 text-[10px] leading-relaxed text-[#A3A3A3]/80">
              Sandbox only — data resets daily and features may be limited. Ready to run your practice for real? <Link to="/register" className="text-[#38BDF8] hover:underline">Create your own account →</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
